import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Autocomplete } from "@react-google-maps/api";
import { loadGoogleAutoCompleteApi } from "@SupplyLand/Handlers/LoadGoogleAutoCompleteApi";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    handleGooglePlaceResult: (placeResult: google.maps.places.PlaceResult | undefined) => void;
    address1: string;
    onChangeAddress1: TextFieldProps["onChange"];
    error?: React.ReactNode;
}

type GooglePlacesAutoCompleteProps = OwnProps;

interface GooglePlacesAutoCompleteStyles {
    wrapper: InjectableCss;
    inputField: TypographyPresentationProps;
}

const googlePlacesAutoCompleteStyles: GooglePlacesAutoCompleteStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    inputField: {
        css: css`
            width: 100%;
            padding: 4px;
        `,
    },
};

const styles = googlePlacesAutoCompleteStyles;

const GooglePlacesAutoComplete = ({
    handleGooglePlaceResult,
    address1,
    onChangeAddress1,
    error,
}: GooglePlacesAutoCompleteProps) => {
    const [googleApiLoaded, setGoogleApiLoaded] = useState<boolean>(false);
    const [searchResult, setSearchResult] = useState<google.maps.places.Autocomplete>();
    const [placeResult, setPlaceResult] = useState<google.maps.places.PlaceResult>();

    const autocompleteOptions = {
        types: ["address"],
        componentRestrictions: { country: ["us"] },
        fields: ["place_id", "geometry", "name", "address_components"],
    };

    useEffect(() => {
        if (!googleApiLoaded) {
            loadGoogleAutoCompleteApi();
            setGoogleApiLoaded(true);
        }
    }, []);

    useEffect(() => {
        const wait = (ms: number) => {
            return new Promise(resolve => {
                setTimeout(resolve, ms);
            });
        };

        async function alterPacContainer() {
            await wait(500);
            const pacContainerEls = document.getElementsByClassName("pac-container pac-logo");
            for (let i = 0; i < pacContainerEls.length; i++) {
                const pacContainer = pacContainerEls[i];
                pacContainer.setAttribute("style", "z-index: 10000; display: none;");
            }
            const pacTargetInputs = document.getElementsByClassName("pac-target-input");
            for (let i = 0; i < pacTargetInputs.length; i++) {
                const pacTargetInput = pacTargetInputs[i];
                pacTargetInput.setAttribute("autocomplete", "on");
            }
        }

        if (googleApiLoaded) {
            alterPacContainer();
        }
    }, [googleApiLoaded]);

    useEffect(() => {
        if (handleGooglePlaceResult) {
            handleGooglePlaceResult(placeResult);
        }
    }, [placeResult]);

    function onLoad(autocomplete: google.maps.places.Autocomplete) {
        setSearchResult(autocomplete);
    }

    function locationSelected() {
        if (searchResult) {
            const place = searchResult.getPlace();
            setPlaceResult(place);
        }
    }

    function onBlur() {
        const pacContainerEls = document.getElementsByClassName("pac-container pac-logo");
        for (let i = 0; i < pacContainerEls.length; i++) {
            const pacContainer = pacContainerEls[i];
            pacContainer.setAttribute("style", "z-index: 10000; display: none;");
        }
    }
    return (
        <StyledWrapper id="searchColumn" {...styles.wrapper}>
            <Autocomplete options={autocompleteOptions} onLoad={onLoad} onPlaceChanged={locationSelected}>
                <TextField
                    id="googleAutocompleteInput"
                    type="text"
                    placeholder="Address 1"
                    value={address1}
                    onChange={onChangeAddress1}
                    onBlur={onBlur}
                    onKeyPress={e => {
                        e.key === "Enter" && e.preventDefault();
                    }}
                    error={error}
                />
            </Autocomplete>
        </StyledWrapper>
    );
};

export default GooglePlacesAutoComplete;
